import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Printer, UserSearch } from 'lucide-react';
import { ErrorState } from '@/components/common/ErrorState';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PCMP_PERMISSION } from '../api/pcmpAccess.api';
import { downloadPcmpMoReportXlsx, pcmpMoReportQueryOptions } from '../api/pcmpMoReport.api';
import { PcmpExportButton } from '../components/PcmpQueue/PcmpExportButton';
import { PcmpMoReportFilterBar } from '../components/PcmpReports/PcmpMoReportFilterBar';
import { PcmpMoReportTable } from '../components/PcmpReports/PcmpMoReportTable';
import { usePcmpMoReportFilters } from '../hooks/usePcmpMoReportFilters';
import { usePcmpPermissions } from '../hooks/usePcmpPermissions';
import { pcmpMoLabel, pcmpReportingLabelFromScope } from '../utils/pcmpMoReportLabels';

/** Proposals and payments of one MO, or of many MOs, by project (LDS-743). */
export function PcmpMoReportPage() {
  const controller = usePcmpMoReportFilters();
  const { appliedFilters } = controller;
  const { can } = usePcmpPermissions();
  const canExport = can(PCMP_PERMISSION.VIEW_ALL, PCMP_PERMISSION.EXPORT);

  const { data, isFetching, isPlaceholderData, error, refetch } = useQuery(
    pcmpMoReportQueryOptions(appliedFilters),
  );
  // The report of the filters on screen, not the previous one kept while it loads.
  const current = isPlaceholderData ? undefined : data;
  const pickNames = {
    mo: current?.mo ? pcmpMoLabel(current.mo) : undefined,
    reporting: current ? pcmpReportingLabelFromScope(current.scopeLabel) : undefined,
  };

  const { scope, projectIds, ...rest } = appliedFilters;
  const printSearch = { scope: scope === 'MO' ? undefined : scope, projectIds, ...rest };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">MO Wise Report</h1>
          <p className="text-sm text-muted-foreground">
            Proposals and payments of one MO by project, or one row per MO.
          </p>
        </div>
        {canExport ? (
          <div className="flex flex-wrap items-center gap-2">
            <PcmpExportButton
              disabled={!data || !!data.message || isPlaceholderData}
              download={() =>
                data ? downloadPcmpMoReportXlsx(appliedFilters, data) : Promise.resolve()
              }
            />
            {data && !data.message ? (
              <Link
                to="/pcmp/reports/mo-wise/print"
                search={printSearch}
                target="_blank"
                className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
              >
                <Printer className="size-3.5" />
                Print / PDF
              </Link>
            ) : null}
          </div>
        ) : null}
      </div>

      <PcmpMoReportFilterBar controller={controller} pickNames={pickNames} />

      {error && !data ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load the MO Wise report"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      ) : data?.message ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border bg-card p-10 text-center shadow-sm">
          <UserSearch className="size-8 text-muted-foreground" aria-hidden />
          <p className="text-sm text-muted-foreground">{data.message}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2 rounded-xl border bg-card p-4 shadow-sm">
          <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
            <span className="font-medium">
              {data ? `${data.scopeLabel} · ${data.projectLabel}` : 'Loading…'}
            </span>
            <span className="text-muted-foreground">{data?.periodLabel}</span>
          </div>
          {data ? (
            <div
              className={cn('overflow-x-auto', isFetching && 'opacity-60 transition-opacity')}
              aria-busy={isFetching}
            >
              <PcmpMoReportTable report={data} />
            </div>
          ) : (
            <div className="h-48 animate-pulse rounded-md bg-muted" aria-hidden />
          )}
        </div>
      )}
    </div>
  );
}
